import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { institutionLabel } from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/namedRefLabel';
import type {
  MatchedInstitution,
  OperatorContextRoot,
  OperatorRow as OperatorFleetRow,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';
import type { OperatorContextSelection } from '@backrs_ui/grow-ui/src/redux/operatorContext';

import {
  Box,
  Button,
  Chip,
  type SxProps,
  TableCell,
  TableRow,
  type Theme,
  Tooltip,
  Typography,
} from '@mui/material';

import { errorTagSx } from './errorTag';
import { hasIssue, operatorHealth } from './operatorHealth';

const strings = appStrings.modules.rostersAdmin.operators;

export const OPERATOR_HEADERS: readonly string[] = [
  strings.columns.name,
  strings.columns.type,
  strings.columns.agreements,
  strings.columns.admins,
  strings.columns.rosters,
  strings.columns.overCap,
  strings.columns.lapsingSoon,
  strings.columns.lapse,
  strings.columns.coverage,
];

export const OPERATOR_TABLE_ARIA = strings.tableAria;

type OperatorRowProps = Readonly<{
  operator: OperatorFleetRow;
  activeOperatorId: OperatorContextSelection | null;
  onOpen: (operator: OperatorFleetRow) => void;
  onOpenMatchedInstitution: (operator: OperatorFleetRow, institution: MatchedInstitution) => void;
}>;

const Count = ({ id, value }: { id: string; value: number }) => (
  <Typography id={id} variant="text-sm-medium" sx={value > 0 ? flaggedSx : undefined}>
    {value}
  </Typography>
);

export const OperatorRow = ({
  operator,
  activeOperatorId,
  onOpen,
  onOpenMatchedInstitution,
}: OperatorRowProps) => {
  const name = institutionLabel(operator);
  const isActive = operator.id === activeOperatorId;
  const health = operatorHealth(operator);
  const { seats, isOverCap, isLapsingSoon, isLapsed } = health;
  const isIssue = hasIssue(health);

  return (
    <TableRow
      id={`rosters-admin-operator-row-${operator.id}`}
      sx={isIssue ? issueRowSx : isActive ? activeRowSx : undefined}
    >
      <TableCell>
        <Box sx={nameCellSx}>
          <Box sx={nameLineSx}>
            <Button
              id={`rosters-admin-operator-open-${operator.id}`}
              hierarchy="Tertiary color"
              size="sm"
              aria-label={strings.openAria(name)}
              onClick={() => onOpen(operator)}
            >
              {name}
            </Button>
            {isActive ? (
              <Chip
                id={`rosters-admin-operator-active-${operator.id}`}
                label={strings.activeMarker}
                variant="tag"
                size="sm"
              />
            ) : null}
          </Box>
          {operator.matchedInstitutions.length > 0 ? (
            <Box sx={matchesSx}>
              <Typography variant="text-xs-regular" sx={mutedSx}>
                {strings.matchedInstitutions}
              </Typography>
              {operator.matchedInstitutions.map((institution) => (
                <Button
                  key={institution.id}
                  id={`rosters-admin-operator-${operator.id}-institution-${institution.id}`}
                  hierarchy="Tertiary color"
                  size="sm"
                  aria-label={strings.openChildAria(institutionLabel(institution), name)}
                  onClick={() => onOpenMatchedInstitution(operator, institution)}
                >
                  {institutionLabel(institution)}
                </Button>
              ))}
            </Box>
          ) : null}
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="text-sm-regular">{operator.type ?? ''}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="text-sm-regular">{operator.activeAgreements}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="text-sm-regular">{operator.admins}</Typography>
      </TableCell>
      <TableCell>
        <Typography id={`rosters-admin-operator-rosters-${operator.id}`} variant="text-sm-regular">
          {operator.rosters}
        </Typography>
      </TableCell>
      <TableCell>
        <Count id={`rosters-admin-operator-over-cap-${operator.id}`} value={operator.overCap} />
      </TableCell>
      <TableCell>
        <Count
          id={`rosters-admin-operator-lapsing-soon-${operator.id}`}
          value={operator.lapsingSoon}
        />
      </TableCell>
      <TableCell>
        <Count id={`rosters-admin-operator-lapse-${operator.id}`} value={operator.lapse} />
      </TableCell>
      <TableCell>
        <Box id={`rosters-admin-operator-coverage-${operator.id}`} sx={coverageSx}>
          {seats === null ? (
            <Typography variant="text-xs-regular" sx={mutedSx}>
              {strings.coverageUnavailable}
            </Typography>
          ) : (
            <>
              <Typography
                id={`rosters-admin-operator-coaches-${operator.id}`}
                variant="text-xs-regular"
              >
                {strings.coaches(seats.coaches)}
              </Typography>
              <Typography
                id={`rosters-admin-operator-students-${operator.id}`}
                variant="text-xs-regular"
              >
                {strings.students(seats.students)}
              </Typography>
            </>
          )}
          {isIssue ? (
            <Box sx={pillsSx}>
              {isOverCap ? (
                <Chip
                  id={`rosters-admin-operator-over-cap-pill-${operator.id}`}
                  label={strings.overCap}
                  variant="tag"
                  size="sm"
                  sx={errorTagSx}
                />
              ) : null}
              {isLapsingSoon ? (
                <Tooltip title={strings.lapsingSoonHelp} placement="top" arrow describeChild>
                  <Chip
                    id={`rosters-admin-operator-lapsing-soon-pill-${operator.id}`}
                    label={strings.lapsingSoon}
                    variant="tag"
                    size="sm"
                    sx={errorTagSx}
                  />
                </Tooltip>
              ) : null}
              {isLapsed ? (
                <Tooltip title={strings.lapsedHelp} placement="top" arrow describeChild>
                  <Chip
                    id={`rosters-admin-operator-lapsed-pill-${operator.id}`}
                    label={strings.lapsed}
                    variant="tag"
                    size="sm"
                    sx={errorTagSx}
                  />
                </Tooltip>
              ) : null}
            </Box>
          ) : null}
        </Box>
      </TableCell>
    </TableRow>
  );
};

type OperatorHomeRowProps = Readonly<{
  home: OperatorContextRoot;
  isActive: boolean;
  onOpen: () => void;
}>;

/** The caller's own anchor, pinned above the fleet. It holds no agreements, so it carries
 * no counts. */
export const OperatorHomeRow = ({ home, isActive, onOpen }: OperatorHomeRowProps) => {
  const name = institutionLabel(home);

  return (
    <TableRow id="rosters-admin-operator-home-row" sx={isActive ? activeRowSx : undefined}>
      <TableCell>
        <Box sx={nameLineSx}>
          <Button
            id="rosters-admin-operator-home-open"
            hierarchy="Tertiary color"
            size="sm"
            aria-label={strings.openAria(name)}
            onClick={isActive ? undefined : onOpen}
          >
            {name}
          </Button>
          <Chip
            id="rosters-admin-operator-home-tag"
            label={strings.homeMarker}
            variant="tag"
            size="sm"
          />
          {isActive ? (
            <Chip
              id="rosters-admin-operator-active-home"
              label={strings.activeMarker}
              variant="tag"
              size="sm"
            />
          ) : null}
        </Box>
      </TableCell>
      <TableCell colSpan={OPERATOR_HEADERS.length - 1} />
    </TableRow>
  );
};

const nameCellSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-xxs'],
});

const nameLineSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const activeRowSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-active'],
});

const matchesSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-xxs'],
  paddingLeft: backrs.spacing['spacing-md'],
});

const coverageSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
});

const pillsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-xs'],
});

const issueRowSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-error-primary'],
});

const mutedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const flaggedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-error-primary'],
});
